import { Loader2, Package, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  AdminBulkActionRow,
  AdminSectionCard,
  ConfirmDialog,
  EmptyState,
  HelpAnchor,
  InlineError,
  Modal,
  Pill,
  SkeletonRows,
  Switch,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { BUTTON_GHOST_ADMIN } from '@/shared/ui-class';
import {
  SEED_PREDEFINED_SKILLS_MANIFEST,
  type PredefinedSkillDef,
  type PredefinedSkillsManifest,
} from '@/versions/v1/shared/admin-seed';
import { AdminRowMenu } from './AdminRowMenu';
import { AdminSceneShell } from './admin-shared';
import {
  bodyBytes,
  countEnabled,
  emptySkillDef,
  fromFormValues,
  PREDEFINED_SKILLS_MAX_COUNT,
  PREDEFINED_SKILL_BODY_MAX_BYTES,
  removeSkill,
  SLUG_PATTERN,
  toFormValues,
  toggleSkillEnabled,
  validateModalDraft,
} from './PredefinedSkillsScene.transform';
import { SkillCreateForm } from './SkillCreateForm';

type ModalState = { mode: 'add' } | { mode: 'edit'; index: number };

export function PredefinedSkillsScene() {
  const [manifest, setManifest] = useState<PredefinedSkillsManifest>(
    SEED_PREDEFINED_SKILLS_MANIFEST,
  );
  const [loading] = useState(false);
  const [error] = useState<string | null>(null);
  const [actionError] = useState<string | null>(null);
  const [savingSlugs, setSavingSlugs] = useState<Set<string>>(new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [modalState, setModalState] = useState<ModalState | null>(null);
  const [modalDraft, setModalDraft] = useState<PredefinedSkillDef | null>(null);
  const [modalEnabled, setModalEnabled] = useState(true);
  const [modalSubmitting, setModalSubmitting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [removeSlug, setRemoveSlug] = useState<string | null>(null);
  const [removeSubmitting, setRemoveSubmitting] = useState(false);

  const enabledCount = countEnabled(manifest);
  const totalCount = manifest.skills.length;
  const remaining = PREDEFINED_SKILLS_MAX_COUNT - totalCount;
  const isPersisting = savingSlugs.size > 0;

  const openAddModal = () => {
    if (totalCount >= PREDEFINED_SKILLS_MAX_COUNT || isPersisting) return;
    setModalDraft(emptySkillDef());
    setModalEnabled(true);
    setModalState({ mode: 'add' });
    setModalError(null);
    setModalOpen(true);
  };

  const openEditModal = (index: number) => {
    if (isPersisting) return;
    setModalDraft({ ...manifest.skills[index] });
    setModalEnabled(manifest.skills[index].enabled);
    setModalState({ mode: 'edit', index });
    setModalError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalState(null);
    setModalDraft(null);
    setModalError(null);
    setModalSubmitting(false);
  };

  const modalValidationError = useMemo(
    () => validateModalDraft(modalDraft, modalState?.mode ?? null),
    [modalDraft, modalState],
  );

  const toggleEnabled = (slug: string) => {
    if (isPersisting || modalOpen) return;
    setSavingSlugs((prev) => new Set(prev).add(slug));
    setManifest((prev) => toggleSkillEnabled(prev, slug));
    setSavingSlugs((prev) => {
      const next = new Set(prev);
      next.delete(slug);
      return next;
    });
  };

  const handleModalSubmit = () => {
    if (!modalDraft || !modalState || isPersisting) return;
    if (modalValidationError) {
      setModalError(modalValidationError);
      return;
    }
    const values = toFormValues(modalDraft);
    const nextSkill = fromFormValues(values, modalEnabled);
    if (!nextSkill) {
      setModalError('Invalid form.');
      return;
    }

    let nextSkills = [...manifest.skills];
    if (modalState.mode === 'add') {
      if (nextSkills.some((skill) => skill.slug === nextSkill.slug)) {
        setModalError('A skill with this slug already exists.');
        return;
      }
      nextSkills.push(nextSkill);
    } else {
      nextSkills[modalState.index] = nextSkill;
    }

    setModalSubmitting(true);
    setModalError(null);
    setManifest({ skills: nextSkills });
    closeModal();
  };

  const handleRemove = () => {
    if (!removeSlug || isPersisting) return;
    setRemoveSubmitting(true);
    setManifest((prev) => removeSkill(prev, removeSlug));
    setRemoveSlug(null);
    setRemoveSubmitting(false);
  };

  const modalFormValues = modalDraft ? toFormValues(modalDraft) : null;

  return (
    <AdminSceneShell
      sceneName="PredefinedSkillsScene"
      title="Skills library"
      description="Platform-wide predefined skills available to every user."
      headerActions={<HelpAnchor doc="workspace/admin" />}
      stackClassName="space-y-6"
    >
      <Commentable name="Panel">
        <div className="space-y-6">
          <AdminSectionCard tone="info">
            <p className="text-sm font-medium text-zinc-800 dark:text-zinc-200">
              Baseline skills pool — inherited by every user
            </p>
            <p className="mt-1 text-sm text-muted">
              These platform skills (PowerPoint, Word, Daytona, doc-editing, etc.) are
              seeded into every user account automatically. Users see them as read-only —
              they can&apos;t remove or edit them directly (editing creates their own
              personal copy instead). Users append their own skills on top of this
              baseline.{' '}
              <strong className="font-medium text-zinc-700 dark:text-zinc-300">
                Disabled skills are invisible to agents
              </strong>{' '}
              — they are removed from every user&apos;s skill set and will not appear in
              the agent&apos;s skill search list.
            </p>
          </AdminSectionCard>

          {error ? <InlineError>{error}</InlineError> : null}
          {actionError ? <InlineError>{actionError}</InlineError> : null}

          {loading ? (
            <SkeletonRows count={6} cols={4} />
          ) : (
            <>
              <AdminBulkActionRow
                left={
                  <>
                    {totalCount} skill{totalCount === 1 ? '' : 's'} · {enabledCount}{' '}
                    enabled · {remaining} remaining
                  </>
                }
              >
                <button
                  type="button"
                  onClick={openAddModal}
                  disabled={
                    totalCount >= PREDEFINED_SKILLS_MAX_COUNT || modalOpen || isPersisting
                  }
                  className={BUTTON_GHOST_ADMIN}
                >
                  <Plus className="h-4 w-4" />
                  Add skill
                </button>
              </AdminBulkActionRow>

              <AdminSectionCard title="Skills">
                {manifest.skills.length === 0 ? (
                  <EmptyState
                    embedded
                    icon={<Package className="mx-auto h-8 w-8 text-zinc-400" />}
                    title="No skills defined yet."
                  />
                ) : (
                  <Table wrap={false}>
                    <THead>
                      <TR>
                        <TH>Skill</TH>
                        <TH>Description</TH>
                        <TH className="text-right">Enabled</TH>
                        <TH className="w-10" aria-label="Actions" />
                      </TR>
                    </THead>
                    <TBody>
                      {manifest.skills.map((skill, index) => {
                        const rowBusy = savingSlugs.has(skill.slug);
                        return (
                          <TR key={skill.slug}>
                            <TD>
                              <p className="font-medium text-zinc-900 dark:text-zinc-100">
                                {skill.name || skill.slug}
                              </p>
                              <code className="text-xs text-muted">{skill.slug}</code>
                            </TD>
                            <TD>
                              <p className="line-clamp-2 text-zinc-600 dark:text-zinc-300">
                                {skill.description}
                              </p>
                            </TD>
                            <TD className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                {!skill.enabled ? <Pill tone="danger">Off</Pill> : null}
                                {rowBusy ? (
                                  <Loader2
                                    className="h-4 w-4 animate-spin text-zinc-400"
                                    aria-label="Saving"
                                  />
                                ) : null}
                                <Switch
                                  checked={skill.enabled}
                                  onCheckedChange={() => toggleEnabled(skill.slug)}
                                  disabled={isPersisting || modalOpen}
                                  aria-label={
                                    skill.enabled
                                      ? `Disable ${skill.slug}`
                                      : `Enable ${skill.slug}`
                                  }
                                />
                              </div>
                            </TD>
                            <TD className="text-right">
                              <AdminRowMenu
                                label="Skill actions"
                                items={[
                                  {
                                    label: 'Edit',
                                    onSelect: () => openEditModal(index),
                                    disabled: modalOpen || isPersisting,
                                  },
                                  {
                                    label: 'Remove',
                                    onSelect: () => setRemoveSlug(skill.slug),
                                    disabled: modalOpen || isPersisting,
                                    danger: true,
                                  },
                                ]}
                              />
                            </TD>
                          </TR>
                        );
                      })}
                    </TBody>
                  </Table>
                )}
              </AdminSectionCard>
            </>
          )}

          {modalDraft && modalFormValues ? (
            <Modal
              open={modalOpen}
              onOpenChange={(open) => {
                if (!open) closeModal();
                else setModalOpen(true);
              }}
              title={modalState?.mode === 'edit' ? 'Edit skill' : 'Add skill'}
              placement="center"
            >
              <SkillCreateForm
                values={modalFormValues}
                onChange={(next) => {
                  setModalError(null);
                  setModalDraft((prev) =>
                    prev
                      ? {
                          ...prev,
                          slug: next.slug,
                          name: next.name,
                          description: next.description,
                          body: next.body,
                        }
                      : prev,
                  );
                }}
                onSubmit={handleModalSubmit}
                mode="diy"
                submitting={modalSubmitting}
                error={modalError}
                validationError={modalValidationError}
                showName
                enabled={modalEnabled}
                onEnabledChange={setModalEnabled}
                editMode={modalState?.mode === 'edit'}
                opaqueInputs
                hideHeader
                onCancel={closeModal}
                bodyLabel="Content"
                bodyRows={12}
                slugPattern={SLUG_PATTERN}
                bodyHint={
                  <p
                    className={cn(
                      'text-xs',
                      bodyBytes(modalDraft.body) > PREDEFINED_SKILL_BODY_MAX_BYTES
                        ? 'text-red-600'
                        : 'text-muted',
                    )}
                  >
                    {(bodyBytes(modalDraft.body) / 1024).toFixed(1)} /{' '}
                    {(PREDEFINED_SKILL_BODY_MAX_BYTES / 1024).toFixed(0)} KB
                  </p>
                }
              />
            </Modal>
          ) : null}

          <ConfirmDialog
            open={removeSlug !== null}
            onOpenChange={(open) => {
              if (!open && !removeSubmitting) setRemoveSlug(null);
            }}
            title="Remove skill"
            description={
              removeSlug ? (
                <>
                  Remove <code>{removeSlug}</code> from the baseline manifest? It will
                  disappear from every user&apos;s skill set.
                </>
              ) : null
            }
            confirmLabel="Remove"
            tone="danger"
            onConfirm={handleRemove}
          />
        </div>
      </Commentable>
    </AdminSceneShell>
  );
}
